// src/pages/Login.js

import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert, 
  Grid, 
  Paper, 
  Avatar, 
  Checkbox, 
  FormControlLabel, 
  CircularProgress 
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Login() {
  const { t } = useTranslation()  // i18n Hook

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { login, logout, currentUser } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setError('')

      const userCredential = await login(email, password)

      // Email onaylı mı kontrol edelim
      if (!userCredential.user.emailVerified) {
        await logout()
        setError(t('loginPage.errorEmailVerification'))
        return
      }

      setLoading(true)
    } catch (err) {
      setError(t('loginPage.errorLogin'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser) {
      // currentUser gelince login tamam demektir
      navigate('/')
    }
  }, [currentUser, navigate])

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fdfbfb 0%, #f3f4f6 40%, #e0f2fe 100%)',
        px: 2
      }}
    >
      <Container component="main" maxWidth="xs" disableGutters>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            boxShadow: '0 18px 45px rgba(15,23,42,0.18)',
            backgroundColor: 'rgba(255,255,255,0.96)',
            border: '1px solid rgba(148,163,184,0.25)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'primary.main',
                boxShadow: '0 10px 25px rgba(59,130,246,0.45)'
              }}
            >
              <LockOutlinedIcon />
            </Avatar>

            <Box sx={{ textAlign: 'center' }}>
              <Typography component="h1" variant="h5" sx={{ fontWeight: 700 }}>
                {t('loginPage.title')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t('loginPage.subtitle') || ''}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity="error"
                sx={{ width: '100%', mt: 1.5, borderRadius: 3 }}
              >
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ mt: 2, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                required
                fullWidth
                type="email"
                label={t('loginPage.emailLabel')}
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'rgba(248,250,252,0.9)'
                  }
                }}
              />

              <TextField
                required
                fullWidth
                type="password"
                label={t('loginPage.passwordLabel')}
                variant="outlined"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'rgba(248,250,252,0.9)'
                  }
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 0.5
                }}
              >
                <FormControlLabel
                  control={<Checkbox value="remember" color="primary" size="small" />}
                  label={t('loginPage.rememberMe')}
                />

                <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                    {t('loginPage.forgotPassword')}
                  </Typography>
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{
                  mt: 1.5,
                  mb: 0.5,
                  py: 1.3,
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  boxShadow: '0 14px 30px rgba(37,99,235,0.4)'
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('loginPage.loginButton')
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('loginPage.noAccount')} {' '}
                  <Link to="/register" style={{ textDecoration: 'none' }}>
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary.main"
                      sx={{ fontWeight: 600 }}
                    >
                      {t('loginPage.registerPrompt')}
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}
